import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './AdvancedSearchModal.css';
import { HistoryFilters } from '@/services/historyService';
import SearchableSelect from './SearchableSelect';
import optionsService from '@/services/optionsService';

interface AdvancedSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: HistoryFilters) => void;
  currentFilters: HistoryFilters;
  isLoading?: boolean;
  resultsCount?: number;
}

interface DateRange {
  label: string;
  value: string;
  startDate?: string;
  endDate?: string;
}

const AdvancedSearchModal: React.FC<AdvancedSearchModalProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  currentFilters,
  isLoading = false,
  resultsCount = 0
}) => {
  const [filters, setFilters] = useState<HistoryFilters>(currentFilters);
  const [customDateRange, setCustomDateRange] = useState({
    start: null as Date | null,
    end: null as Date | null
  });
  const [selectedDateRange, setSelectedDateRange] = useState<string>('');
  const [showPeriodOptions, setShowPeriodOptions] = useState(false);
  
  // États pour les options des SearchableSelect
  const [vehiculeOptions, setVehiculeOptions] = useState<{value: string, label: string}[]>([]);
  const [remorqueOptions, setRemorqueOptions] = useState<{value: string, label: string}[]>([]);
  const [chauffeurOptions, setChauffeurOptions] = useState<{value: string, label: string}[]>([]);
  const [convoyeurOptions, setConvoyeurOptions] = useState<{value: string, label: string}[]>([]);
  const [clientOptions, setClientOptions] = useState<{value: string, label: string}[]>([]);
  const [fournisseurOptions, setFournisseurOptions] = useState<{value: string, label: string}[]>([]);
  const [transporteurOptions, setTransporteurOptions] = useState<{value: string, label: string}[]>([]);
  const [exportateurOptions, setExportateurOptions] = useState<{value: string, label: string}[]>([]);
  const [magasinOptions, setMagasinOptions] = useState<{value: string, label: string}[]>([]);
  const [conditionnementOptions, setConditionnementOptions] = useState<{value: string, label: string}[]>([]);

  const typeVehiculeOptions = [
    { value: '', label: 'Tous les types de véhicule' },
    { value: 'Vehicule', label: 'Véhicule' },
    { value: 'Fourchette', label: 'Fourchette' },
    { value: 'Tricycle', label: 'Tricycle' },
    { value: 'Vanette', label: 'Vanette' },
    { value: 'Autre', label: 'Autre' },
  ];

  // Périodes prédéfinies
  const predefinedRanges: DateRange[] = [
    { label: 'Aujourd\'hui', value: 'today' },
    { label: 'Hier', value: 'yesterday' },
    { label: 'Les 7 derniers jours', value: 'last_7_days' },
    { label: 'Les 15 derniers jours', value: 'last_15_days' },
    { label: 'Les 30 derniers jours', value: 'last_30_days' },
    { label: 'Cette année', value: 'this_year' },
    { label: 'L\'année dernière', value: 'last_year' },
    { label: 'Période personnalisée', value: 'custom' }
  ];

  // Types de pesée
  const peseeTypes = [
    { value: '', label: 'Tous les types' },
    { value: 'ENTREE', label: 'Entrée' },
    { value: 'SORTIE', label: 'Sortie' },
    { value: 'TRANSFERT', label: 'Transfert' }
  ];

  // Produits
  const products = [
    { value: '', label: 'Tous les produits' },
    { value: 'cacao', label: 'Cacao en fèves' },
    { value: 'cafe', label: 'Café robusta' },
    { value: 'anacarde', label: 'Anacarde' },
    { value: 'coton', label: 'Coton' }
  ];

  // Statuts
  const statuses = [
    { value: '', label: 'Tous les statuts' },
    { value: 'EN_COURS', label: 'En cours' },
    { value: 'EN_ATTENTE_TARE', label: 'En attente tare' },
    { value: 'TARE_ENREGISTREE', label: 'Tare enregistrée' },
    { value: 'BRUT_ENREGISTRE', label: 'Brut enregistré' },
    { value: 'COMPLETEE', label: 'Complétée' },
    { value: 'ACHEVEE', label: 'Achevée' },
    { value: 'REFOULEE', label: 'Refoulée' }
  ];

  useEffect(() => {
    if (isOpen) {
      setFilters(currentFilters);
      // Initialiser les dates par défaut (30 derniers jours)
      const today = new Date();
      const last30Days = new Date(today);
      last30Days.setDate(last30Days.getDate() - 30);
      
      setCustomDateRange({
        start: last30Days,
        end: today
      });
      
      // Charger les options depuis le backend
      loadOptions();
    }
  }, [isOpen, currentFilters]);

  // Fonction pour charger les options depuis le backend
  const loadOptions = async () => {
    try {
      const options = await optionsService.getAllOptions();
      setVehiculeOptions(options.vehicules);
      setRemorqueOptions(options.remorques);
      setChauffeurOptions(options.chauffeurs);
      setConvoyeurOptions(options.convoyeurs);
      setClientOptions(options.clients);
      setFournisseurOptions(options.fournisseurs);
      setTransporteurOptions(options.transporteurs);
      setExportateurOptions(options.exportateurs);
      setMagasinOptions(options.magasins || []);
      setConditionnementOptions(options.conditionnements || []);
    } catch (error) {
      console.error('Erreur lors du chargement des options:', error);
    }
  };

  const handleDateRangeChange = (range: DateRange) => {
    setSelectedDateRange(range.value);
    
    if (range.value === 'custom') {
      return; // Ne pas modifier les dates pour la période personnalisée
    }

    const today = new Date();
    let startDate = '';
    let endDate = '';

    switch (range.value) {
      case 'today':
        startDate = endDate = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = endDate = yesterday.toISOString().split('T')[0];
        break;
      case 'last_7_days':
        const last7Days = new Date(today);
        last7Days.setDate(last7Days.getDate() - 7);
        startDate = last7Days.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'last_15_days':
        const last15Days = new Date(today);
        last15Days.setDate(last15Days.getDate() - 15);
        startDate = last15Days.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'last_30_days':
        const last30Days = new Date(today);
        last30Days.setDate(last30Days.getDate() - 30);
        startDate = last30Days.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'this_year':
        startDate = `${today.getFullYear()}-01-01`;
        endDate = today.toISOString().split('T')[0];
        break;
      case 'last_year':
        const lastYear = today.getFullYear() - 1;
        startDate = `${lastYear}-01-01`;
        endDate = `${lastYear}-12-31`;
        break;
    }

    // Mettre à jour les dates dans les champs de date
    const startDateObj = startDate ? new Date(startDate) : null;
    const endDateObj = endDate ? new Date(endDate) : null;
    
    setCustomDateRange({
      start: startDateObj,
      end: endDateObj
    });

    setFilters(prev => ({
      ...prev,
      date_from: startDate,
      date_to: endDate
    }));
  };

  const handleCustomDateChange = (field: 'start' | 'end', date: Date | null) => {
    setCustomDateRange(prev => ({
      ...prev,
      [field]: date
    }));
    
    if (date) {
      const dateString = date.toISOString().split('T')[0];
      setFilters(prev => ({
        ...prev,
        date_from: field === 'start' ? dateString : prev.date_from,
        date_to: field === 'end' ? dateString : prev.date_to
      }));
    }
  };

  const handleFilterChange = (field: keyof HistoryFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleReset = () => {
    const resetFilters: HistoryFilters = {
      search: '',
      produit: '',
      statut: '',
      type_pesee: '',
      date_from: '',
      date_to: '',
      vehicule: '',
      type_vehicule: '',
      remorque: '',
      chauffeur: '',
      convoyeur: '',
      client: '',
      fournisseur: '',
      transporteur: '',
      exportateur: '',
      magasin: '',
      conditionnement: '',
      provenance: '',
      page: 1,
      page_size: 20
    };
    setFilters(resetFilters);
    setCustomDateRange({ start: null, end: null });
    setSelectedDateRange('');
    setShowPeriodOptions(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className="relative ml-auto w-96 h-full bg-white shadow-xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-gray-900">
              <i className="fas fa-search text-blue-500 mr-2"></i>
              Recherche avancée
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
          
          {/* Indicateur de résultats */}
          {resultsCount > 0 && (
            <div className="flex items-center text-sm text-gray-600">
              <i className="fas fa-info-circle text-blue-500 mr-2"></i>
              <span>
                {resultsCount} résultat{resultsCount > 1 ? 's' : ''} trouvé{resultsCount > 1 ? 's' : ''}
              </span>
            </div>
          )}
          
          {isLoading && (
            <div className="flex items-center text-sm text-blue-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              <span>Recherche en cours...</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Mots clés - Premier champ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="fas fa-search text-blue-500 mr-2"></i>
              Mots clés
            </label>
            <input
              type="text"
              placeholder="Rechercher dans toutes les données..."
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Type de pesée */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de pesée
            </label>
            <select
              value={filters.type_pesee || ''}
              onChange={(e) => handleFilterChange('type_pesee', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {peseeTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Produit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Produit
            </label>
            <select
              value={filters.produit || ''}
              onChange={(e) => handleFilterChange('produit', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {products.map(product => (
                <option key={product.value} value={product.value}>{product.label}</option>
              ))}
            </select>
          </div>

          {/* Statut */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statut
            </label>
            <select
              value={filters.statut || ''}
              onChange={(e) => handleFilterChange('statut', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {statuses.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>

          {/* Période de pesée */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Période de pesée
              </label>
              <button
                onClick={() => setShowPeriodOptions(!showPeriodOptions)}
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
              >
                <i className="fas fa-calendar-alt mr-1"></i>
                Périodes prédéfinies
                <i className={`fas fa-chevron-${showPeriodOptions ? 'up' : 'down'} ml-1`}></i>
              </button>
            </div>
            
            {/* Dates par défaut */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Du</label>
                <DatePicker
                  selected={customDateRange.start}
                  onChange={(date) => handleCustomDateChange('start', date)}
                  dateFormat="dd/MM/yyyy"
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  placeholderText="Sélectionner une date"
                  maxDate={new Date()}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Au</label>
                <DatePicker
                  selected={customDateRange.end}
                  onChange={(date) => handleCustomDateChange('end', date)}
                  dateFormat="dd/MM/yyyy"
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  placeholderText="Sélectionner une date"
                  minDate={customDateRange.start || undefined}
                  maxDate={new Date()}
                />
              </div>
            </div>

            {/* Périodes prédéfinies */}
            {showPeriodOptions && (
              <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
                <div className="text-xs text-gray-600 mb-2">Périodes rapides :</div>
                <div className="grid grid-cols-2 gap-2">
                  {predefinedRanges.map(range => (
                    <button
                      key={range.value}
                      onClick={() => handleDateRangeChange(range)}
                      className={`text-left px-2 py-1 rounded text-xs transition-colors ${
                        selectedDateRange === range.value
                          ? 'bg-blue-500 text-white'
                          : 'bg-white text-gray-700 hover:bg-blue-50'
                      }`}
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Transport */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="fas fa-truck text-green-500 mr-2"></i>
              Transport
            </label>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Type de véhicule</label>
                <select
                  value={filters.type_vehicule || ''}
                  onChange={(e) => handleFilterChange('type_vehicule', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  {typeVehiculeOptions.map(opt => (
                    <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <SearchableSelect
                value={filters.vehicule || ''}
                onChange={(value) => handleFilterChange('vehicule', value)}
                options={vehiculeOptions}
                placeholder="Sélectionner un véhicule"
                label="Véhicule"
              />
              <SearchableSelect
                value={filters.remorque || ''}
                onChange={(value) => handleFilterChange('remorque', value)}
                options={remorqueOptions}
                placeholder="Sélectionner une remorque"
                label="Remorque"
              />
              <SearchableSelect
                value={filters.chauffeur || ''}
                onChange={(value) => handleFilterChange('chauffeur', value)}
                options={chauffeurOptions}
                placeholder="Sélectionner un chauffeur"
                label="Chauffeur"
              />
              <SearchableSelect
                value={filters.convoyeur || ''}
                onChange={(value) => handleFilterChange('convoyeur', value)}
                options={convoyeurOptions}
                placeholder="Sélectionner un convoyeur"
                label="Convoyeur"
              />
            </div>
          </div>

          {/* Acteurs */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="fas fa-users text-purple-500 mr-2"></i>
              Acteurs
            </label>
            <div className="space-y-3">
              <SearchableSelect
                value={filters.client || ''}
                onChange={(value) => handleFilterChange('client', value)}
                options={clientOptions}
                placeholder="Sélectionner un client"
                label="Client"
              />
              <SearchableSelect
                value={filters.fournisseur || ''}
                onChange={(value) => handleFilterChange('fournisseur', value)}
                options={fournisseurOptions}
                placeholder="Sélectionner un fournisseur"
                label="Fournisseur"
              />
              <SearchableSelect
                value={filters.transporteur || ''}
                onChange={(value) => handleFilterChange('transporteur', value)}
                options={transporteurOptions}
                placeholder="Sélectionner un transporteur"
                label="Transporteur"
              />
              <SearchableSelect
                value={filters.exportateur || ''}
                onChange={(value) => handleFilterChange('exportateur', value)}
                options={exportateurOptions}
                placeholder="Sélectionner un exportateur"
                label="Exportateur"
              />
            </div>
          </div>

          {/* Stock / conditionnement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="fas fa-warehouse text-amber-500 mr-2"></i>
              Magasin & conditionnement
            </label>
            <div className="space-y-3">
              <SearchableSelect
                value={filters.magasin || ''}
                onChange={(value) => handleFilterChange('magasin', value)}
                options={magasinOptions}
                placeholder="Sélectionner un magasin"
                label="Magasin"
              />
              <SearchableSelect
                value={filters.conditionnement || ''}
                onChange={(value) => handleFilterChange('conditionnement', value)}
                options={conditionnementOptions}
                placeholder="Sélectionner un conditionnement"
                label="Conditionnement"
              />
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Provenance</label>
                <input
                  type="text"
                  value={filters.provenance || ''}
                  onChange={(e) => handleFilterChange('provenance', e.target.value)}
                  placeholder="Lieu de provenance"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex space-x-3">
          <button
            onClick={handleReset}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <i className="fas fa-undo mr-2"></i>
            Réinitialiser
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <i className="fas fa-search mr-2"></i>
            Appliquer
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSearchModal;
